import * as Sentry from "@sentry/browser";
import type { Extras } from "@sentry/core";

import { isInvalidTokenMessage } from "utils/invalidTokenMessage";
import { getFromLocalStorage, saveToLocalStorage } from "utils/localStorage";

import { ACCESS_TOKEN_PARAM } from "const";

// AIDEV-NOTE: Student s tokenem v localStorage se tu nepřesměrovává — „Invalid token“ znamená,
// že se přihlásil jinde, a ukáže mu to modal ze slice `invalidToken` (App.tsx); token smaže až
// jeho zavření a NotAuthenticated se objeví bez reloadu. Rodič a učitel (bez studentského
// tokenu) modal nemají, takže dál rovnou na `/`. Selhaný login handshake ze Zóny vyčistí token
// v Authenticatoru, přesměrování tu není potřeba. [TD-460]
const checkInvalidTokenMessage = (errorMessage: string) => {
  if (!isInvalidTokenMessage(errorMessage)) return;

  const studentToken = getFromLocalStorage<string>(ACCESS_TOKEN_PARAM);
  if (typeof studentToken === "string" && studentToken.length > 0) return;

  saveToLocalStorage(ACCESS_TOKEN_PARAM);
  try {
    window.location.replace("/");
  } catch {
    // Firefox throws an error?!? "Should not already be working."?
    // This is JavaScript, for God's sake. Not some f***ing PHP!
  }
};

const isDoubleFirebaseInitError = (errorMessage: string) =>
  errorMessage.toLocaleLowerCase().includes("the default firebase app already exists");

// Neplatný token není chyba aplikace: `processApiError` studenta odhlásí a přesměruje.
// Do Sentry chodil desítkami tisíc událostí a token nesl přímo v textu. [TD-427]
const isSkippedMessage = (errorMessage: string): boolean =>
  isDoubleFirebaseInitError(errorMessage) || isInvalidTokenMessage(errorMessage);

// RTK Query předává chybu jako serializovaný objekt, ne instanci `Error`; Sentry by
// z něj udělal „Object captured as exception" bez zprávy.
const toError = (error: any, message: string): Error => {
  if (error instanceof Error) return error;

  const converted = new Error(message);
  if (typeof error?.name === "string") converted.name = error.name;

  return converted;
};

// Exportovaný, protože ho AGENTS.md jmenuje jako helper k znovupoužití mimo API chyby.
export const logToSentry = (error: any, extras?: Extras) => {
  try {
    const message = error?.message ?? String(error);
    if (isSkippedMessage(message)) return;

    const jsonStart = message.indexOf("{");
    let errorObject: any = {};

    if (jsonStart !== -1) {
      const jsonString = message.slice(jsonStart);
      try {
        errorObject = JSON.parse(jsonString);
      } catch (parseError) {
        console.warn("Failed to parse JSON from message:", parseError);
      }
    }

    const { request, response } = (typeof errorObject === "object" && errorObject) || {};
    const { errors, status } = response || {};
    const { variables, query } = request || {};

    const operationNameMatch = query?.match(/(mutation|query)\s+(\w+)/);
    const operationName = operationNameMatch?.[2] ?? "unknownOperation";

    // AIDEV-NOTE: Bez pole `errors` to není GraphQL chyba. Se `status` jde o HTTP chybu (např.
    // 502 od proxy) — text chyby je jen hlavička zprávy před JSON, ne celý request s proměnnými
    // operace (u přihlášení handshake Zóny). Bez `status` jde o selhaný přenos (`Load failed`,
    // `Failed to fetch`) nebo nečitelnou odpověď — beze změny. [TD-427]
    if (!Array.isArray(errors)) {
      if (status !== undefined) {
        const httpMessage = message.slice(0, jsonStart).trim().replace(/:$/, "");

        Sentry.withScope(scope => {
          scope.setExtras({ query, variables, status, ...extras, fullError: error });
          Sentry.captureException(new Error(httpMessage), { fingerprint: ["api", "http", operationName, status] });
        });
        return;
      }

      Sentry.withScope(scope => {
        scope.setExtras({ ...extras, fullError: error });
        Sentry.captureException(toError(error, message), { fingerprint: ["api", "transport", message.slice(0, 100)] });
      });
      return;
    }

    const rawErrorMessage = errors.at(0)?.message ?? "Unknown GraphQL error";
    const errorMessage = typeof rawErrorMessage === "string" ? rawErrorMessage : JSON.stringify(rawErrorMessage);

    const rawPath = errors.at(0)?.path;
    const path = Array.isArray(rawPath) ? rawPath.join(".") : "unknownPath";

    const fingerprint = ["graphql", operationName, path, status ?? "unknownStatus", errorMessage.slice(0, 100)];

    Sentry.withScope(scope => {
      scope.setExtras({
        query,
        variables,
        errors,
        ...extras,
        fullError: error,
      });
      Sentry.captureException(new Error(errorMessage), { fingerprint });
    });
  } catch (processError) {
    console.error("Error in processApiError:", processError);
    Sentry.captureException(processError);
  }
};

export const processApiError = (error: any) => {
  if (error === undefined || error === null) return;

  logToSentry(error);

  console.error(error);
  checkInvalidTokenMessage(error.message);
};

interface IRequestData {
  assignmentId: string;
  selfEvaluation: number | null;
  userAnswers: string[] | null;
  timeToSolve: number;
}

export const processExerciseError = (error: any, data: IRequestData) => {
  if (error === undefined || error === null) return;

  const parsedData = { ...data, userAnswers: data.userAnswers?.join(", ") };
  logToSentry(error, parsedData);

  console.error(error);
  checkInvalidTokenMessage(error.message);
};

export const logGeneralExerciseError = (error: unknown, id: string | undefined, data: unknown) => {
  Sentry.withScope(scope => {
    scope.setExtras({ id, data: JSON.stringify(data) });
    Sentry.captureException(error);
  });
};
